// PHASE 3 hardening — PURE row-edit helpers (no React/DOM) so identity + component logic is
// deterministically testable. Used by AIFoodReviewDemo for swap/replace/add.

// structured role -> normal UI component category (final fallback when no real/preferred category)
export const roleToComponent = (role) => ({
  protein: 'protein',
  carb_or_base: 'grains',
  cheese: 'fats_or_rich',
  dairy: 'fats_or_rich',
  fat: 'fats_or_rich',
  sauce: 'dressings_or_sauce',
  veg: 'vegetables',
  fruit: 'vegetables',
  drink: 'drinks',
  other: 'other',
}[role] || 'other');

// resolve the UI component. Order: meaningful returned category -> prompt prefer -> structured role
// mapping -> the row's existing component -> "other" (final fallback). `existing` lets a candidate
// swap keep the row's semantic category when the (often minimal) candidate carries no component.
export const resolveComponent = ({ returned, prefer, role, existing } = {}) => {
  if (returned && returned !== 'other') return returned;
  if (prefer && prefer !== 'other') return prefer;
  if (role) { const m = roleToComponent(role); if (m && m !== 'other') return m; }
  if (existing && existing !== 'other') return existing;
  return 'other';
};

// identity-dependent fields that MUST be replaced together on a swap (never a stale mix)
const IDENTITY_KEYS = [
  'record', 'unit', 'amount', 'perUnit', 'serving_options', 'candidates',
  'fatsecret_food_id', 'external_id', 'match_source', 'brand', 'category', 'component',
];

// Replace an existing row's food identity with a newly chosen food. Keeps the row id and the
// STRUCTURED component identity (concept/role/source_path/substitution_of), but explicitly replaces
// every identity-dependent field from `food` (defaulting to null) so no field referring to the old
// FatSecret item can survive. `food` may be a full search result or a candidate.
export const swapRowIdentity = (row, food) => {
  const next = { ...row };
  IDENTITY_KEYS.forEach((k) => { next[k] = (food[k] !== undefined ? food[k] : null); });
  // component: prefer the food's real category, else structured role mapping, else the row's existing
  // semantic component (a minimal candidate may carry none), else "other".
  next.component = resolveComponent({ returned: food.component, prefer: null, role: row.role, existing: row.component });
  next.status = 'confirmed';
  next.counting_policy = 'auto_count_estimated';
  next.included_in_subtotal_by_default = true;
  next.user_action = 'optional_edit';
  next.portion_estimated = row.portion_estimated || false;
  return next;
};

// Build a brand-new counted row from a chosen food (hidden-ingredient prompt or free add).
// `ctx` may carry structured context {concept, role, source_path} for a structured ingredient.
export const buildAddedRow = (food, ctx = {}, idSeed) => {
  const structured = ctx.source_path === 'structured_component';
  return {
    id: 'add-' + (idSeed != null ? idSeed : Date.now()),
    detected_name: food.record,
    status: 'confirmed',
    counting_policy: 'auto_count_estimated',
    included_in_subtotal_by_default: true,
    user_action: 'optional_edit',
    portion_estimated: false,
    record: food.record,
    unit: food.unit,
    amount: food.amount,
    perUnit: food.perUnit,
    serving_options: food.serving_options || [],
    candidates: food.candidates || [],
    component: resolveComponent({ returned: food.component, prefer: ctx.prefer, role: ctx.role }),
    fatsecret_food_id: food.fatsecret_food_id != null ? food.fatsecret_food_id : null,
    external_id: food.external_id != null ? food.external_id : null,
    match_source: food.match_source || 'fatsecret_search',
    brand: food.brand != null ? food.brand : null,
    category: food.category != null ? food.category : null,
    ...(structured ? {
      source_path: 'structured_component',
      concept: ctx.concept || food.record,
      role: ctx.role || null,
      structured_evidence: 'user_stated',
    } : {}),
  };
};
